import { OPERATORS, OTHER_OPERATOR_SLUG } from '@ev/shared'
import { useCallback, useEffect, useId, useState } from 'react'
import { useColorScheme } from '../app/media'
import { useStrings } from '../i18n'
import {
  POWER_BAND_COLORS,
  POWER_BANDS,
  VOLTAGE_CLASS_COLORS,
  VOLTAGE_CLASSES,
} from '../theme/palette'
import './legend.css'

/**
 * The key to everything the map colours things by.
 *
 * There is one of these. The map task shipped a compact legend floating over
 * the canvas and the detail task shipped a full one in the rail, which meant two
 * legends on screen at once, disagreeing about both the band boundaries and the
 * colours. This is the survivor, and it reads its swatches from
 * `theme/palette.ts` - the same module the MapLibre paint expressions read - so
 * a swatch and a dot cannot drift apart.
 *
 * It is collapsible because a regular visitor learns the palette once and then
 * wants the space back, and the collapsed state is remembered so that decision
 * survives a reload.
 */

const STORAGE_KEY = 'ev:legend:collapsed'

/** Private browsing and blocked storage both throw; neither should break the UI. */
const readCollapsed = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

const writeCollapsed = (collapsed: boolean): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0')
  } catch {
    // A legend that forgets is still a legend.
  }
}

const Swatch = ({ color, round = false }: { color: string; round?: boolean }) => (
  <span
    className={`ev-legend__swatch${round ? ' ev-legend__swatch--round' : ''}`}
    style={{ backgroundColor: color }}
    aria-hidden="true"
  />
)

export const Legend = () => {
  const strings = useStrings()
  const scheme = useColorScheme()
  const bodyId = useId()
  const [collapsed, setCollapsed] = useState(false)

  // Read on mount rather than in the initialiser: the first paint should match
  // the default markup, and storage may be unavailable.
  useEffect(() => setCollapsed(readCollapsed()), [])

  const toggle = useCallback(() => {
    setCollapsed((current) => {
      writeCollapsed(!current)
      return !current
    })
  }, [])

  const bandColors = POWER_BAND_COLORS[scheme]

  return (
    <section className="ev-legend" aria-label={strings.legend.title}>
      <h2 className="ev-legend__heading">
        <button
          type="button"
          className="ev-legend__toggle"
          onClick={toggle}
          aria-expanded={!collapsed}
          aria-controls={bodyId}
        >
          <span className="ev-legend__chevron" data-collapsed={collapsed} aria-hidden="true" />
          {strings.legend.title}
          <span className="visually-hidden">
            {collapsed ? strings.legend.expand : strings.legend.collapse}
          </span>
        </button>
      </h2>

      <div className="ev-legend__body" id={bodyId} hidden={collapsed}>
        <h3 className="ev-legend__group">{strings.legend.power}</h3>
        <p className="ev-legend__note">{strings.legend.powerNote}</p>
        <ul className="ev-legend__list">
          {POWER_BANDS.map((band) => (
            <li key={band} className="ev-legend__item">
              <Swatch color={bandColors[band]} round />
              {strings.powerBand[band]}
            </li>
          ))}
        </ul>
        <p className="ev-legend__note">{strings.legend.brandRing}</p>
        <p className="ev-legend__note">{strings.legend.cluster}</p>

        <h3 className="ev-legend__group">{strings.legend.voltageClasses}</h3>
        <p className="ev-legend__note">{strings.legend.voltageNote}</p>
        <ul className="ev-legend__list">
          {VOLTAGE_CLASSES.map((voltageClass) => (
            <li key={voltageClass} className="ev-legend__item">
              <Swatch color={VOLTAGE_CLASS_COLORS[voltageClass]} />
              {strings.voltageClass[voltageClass]}
            </li>
          ))}
        </ul>

        <h3 className="ev-legend__group">{strings.legend.provenance}</h3>
        <ul className="ev-legend__list ev-legend__list--stacked">
          <li className="ev-legend__item">
            <span className="ev-volt ev-volt--declared ev-volt--sample">400 V</span>
            <span className="ev-legend__caption">{strings.voltage.declaredHint}</span>
          </li>
          <li className="ev-legend__item">
            <span className="ev-volt ev-volt--derived ev-volt--sample">150-500 V</span>
            <span className="ev-legend__caption">{strings.voltage.derivedHint}</span>
          </li>
          <li className="ev-legend__item">
            <span className="ev-volt ev-volt--unknown ev-volt--sample">
              {strings.voltage.unknown}
            </span>
            <span className="ev-legend__caption">{strings.voltage.unknownHint}</span>
          </li>
        </ul>

        <h3 className="ev-legend__group">{strings.legend.operators}</h3>
        <ul className="ev-legend__list ev-legend__list--operators">
          {OPERATORS.map((operator) => (
            <li key={operator.slug} className="ev-legend__item">
              <Swatch color={operator.color} round />
              {operator.slug === OTHER_OPERATOR_SLUG
                ? strings.filters.otherOperators
                : operator.displayName}
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
